/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { AccordionHeader, AccordionItem, Spinner } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { ServiceWithSLA } from "~/components/Runbook/NordcloudServices/ServiceWithSLA";
import { ServiceAttachment } from "~/components/Services/types";
import { ApplicationEnv } from "~/models";
import { useAxios } from "~/tools";

type Props = {
  environment: ApplicationEnv;
};

function ServicesForEnv({ environment }: Props) {
  const { Axios } = useAxios();
  const { showError } = useShowError();

  const [loading, setLoading] = React.useState(true);
  const [serviceAttachments, setServiceAttachments] = React.useState<
    ServiceAttachment[]
  >([]);

  const fetchServices = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(
        `/environments/${environment.id}/services`
      );
      setServiceAttachments(data?.attachedServices);
    } catch (e) {
      showError(e.response);
    }
    setLoading(false);
  }, [Axios, environment, showError, setLoading]);

  React.useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  let servicesNode: React.ReactNode;
  if (loading) {
    servicesNode = <Spinner />;
  } else if (!serviceAttachments.length) {
    servicesNode = <p>No attached services</p>;
  } else {
    servicesNode = serviceAttachments.map((sa) => (
      <ServiceWithSLA serviceAttachment={sa} key={sa.service.id} />
    ));
  }

  return (
    <AccordionHeader title={environment.name}>
      <AccordionItem>{servicesNode}</AccordionItem>
    </AccordionHeader>
  );
}

export { ServicesForEnv };
